import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdOutlineClear, MdOutlineContentCopy, MdOutlineDelete, MdOutlineHistory, MdOutlineUndo } from 'react-icons/md';
import { TbLocationSearch } from 'react-icons/tb';
import { TmdxSwitch } from '@ds';
import { ActionMenu, MenuItemType } from '@ui';
import { LightsAndSirens, NonStopFlightIcon } from '@icons';
import { useAuthContext } from '@lp/core/auth';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { HiOutlineDotsHorizontal } from 'react-icons/hi';
import { useQuoteDetails } from '@lp/features/RoutePlanner/hooks/useQuoteDetails';
import { useLogisticsActions } from '@lp/features/RoutePlanner/hooks/useLogisticsActions';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { getQuoteSuffixByStatus } from '@lp/features/RoutePlanner/utils';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useLightsAndSirensMutation, useRejectAviationMutation, useNonStopFlightMutation } from '@/queries/case';
import { copyToClipboard, isAllowedStatus } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { CaseDetailsPermissions, RouteBuilderPermissions } from '@/permissions';
import { CaseStatus, QuoteStatus, RoutePlanStatus } from '@/api/apis/case';
import { quoteStatusesToRecall } from '../QuoteDetails/constants';
import { RouteTypeEnum } from '@/api/apis/route';

type GetMenuProps = {
  handleLogisticsStatusModal: () => void;
  handleLogisticsChangeStatusLog?: () => void;
  handleQuoteViewModal?: () => void;
  handleCopyLinkAction?: () => void;
  handleRejectAviationModal?: () => void;
  handleLightAndSirens?: () => void;
  handleClearRoute?: () => void;
  handleQuoteRecallModal?: () => void;
  handleDeviceTrackingLog?: () => void;
  handleChangeNonStopFlight?: () => void;
  isLightsSirensRequired?: boolean;
  nonStopFlightRequired?: boolean;
  canViewLightAndSirens: boolean;
  canViewNonStopFlight: boolean;
  isQuoteGenerated?: boolean;
  quoteStatus?: QuoteStatus;
};

const getMenu = ({
  handleLogisticsStatusModal,
  handleLogisticsChangeStatusLog,
  handleQuoteViewModal,
  handleCopyLinkAction,
  handleRejectAviationModal,
  handleLightAndSirens,
  handleClearRoute,
  handleQuoteRecallModal,
  handleDeviceTrackingLog,
  isLightsSirensRequired,
  quoteStatus,
  handleChangeNonStopFlight,
  nonStopFlightRequired,
  canViewLightAndSirens,
  canViewNonStopFlight,
}: GetMenuProps): MenuItemType[] => {
  return [
    ...(canViewNonStopFlight
      ? [
          {
            key: 'set-non-stop-flight',
            type: 'action',
            to: 'set-non-stop-flight',
            label: (
              <div className="flex items-center gap-2">
                <TmdxSwitch
                  color="default"
                  size="sm"
                  isSelected={nonStopFlightRequired}
                  isDisabled={!handleChangeNonStopFlight}
                  onChange={() => handleChangeNonStopFlight?.()}
                  classNames={{
                    base: 'max-w-none w-full flex-row-reverse *:ml-0 justify-between',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-base-20">
                      <NonStopFlightIcon width={16} height={16} />
                    </div>
                    <span className="text-m-r">Non-Stop Flight Required</span>
                  </div>
                </TmdxSwitch>
              </div>
            ),
          },
        ]
      : []),
    ...(canViewLightAndSirens
      ? [
          {
            key: 'set-lights-and-sirens',
            type: 'action',
            to: 'set-lights-and-sirens',
            label: (
              <div className="flex items-center gap-2">
                <TmdxSwitch
                  color="default"
                  size="sm"
                  isSelected={isLightsSirensRequired}
                  isDisabled={!handleLightAndSirens}
                  onChange={() => handleLightAndSirens?.()}
                  classNames={{
                    base: 'max-w-none w-full flex-row-reverse *:ml-0 justify-between',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-base-20">
                      <LightsAndSirens width={16} height={16} />
                    </div>
                    <span className="text-m-r">Lights and sirens for TXC ground leg</span>
                  </div>
                </TmdxSwitch>
              </div>
            ),
          },
        ]
      : []),
    ...(handleRejectAviationModal
      ? [
          {
            key: 'decline-aviation',
            type: 'action',
            to: 'decline-aviation',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineClear size={16} />
                </div>
                <span className="text-m-r">Decline aviation</span>
              </div>
            ),
            onAction: () => handleRejectAviationModal(),
          },
        ]
      : []),
    ...(handleCopyLinkAction
      ? [
          {
            key: 'copy-link',
            type: 'action',
            to: 'copy-link',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineContentCopy size={16} />
                </div>
                <span className="text-m-r">Copy route link</span>
              </div>
            ),
            onAction: () => handleCopyLinkAction(),
          },
        ]
      : []),
    {
      key: 'show-log',
      type: 'action',
      to: 'show-log',
      label: (
        <div className="flex items-center gap-2">
          <div className="text-base-20">
            <MdOutlineHistory size={16} />
          </div>
          <span className="text-m-r">View logistics status change log</span>
        </div>
      ),
      onAction: () => handleLogisticsStatusModal(),
    },
    ...(handleLogisticsChangeStatusLog
      ? [
          {
            key: 'change-log',
            type: 'action',
            to: 'change-log',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineHistory size={16} />
                </div>
                <span className="text-m-r">View logistics details change log</span>
              </div>
            ),
            onAction: () => handleLogisticsChangeStatusLog(),
          },
        ]
      : []),
    ...(handleDeviceTrackingLog
      ? [
          {
            key: 'change-log',
            type: 'action',
            to: 'change-log',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <TbLocationSearch size={16} />
                </div>
                <span className="text-m-r">View device tracking log</span>
              </div>
            ),
            onAction: () => handleDeviceTrackingLog(),
          },
        ]
      : []),
    ...(handleQuoteViewModal
      ? [
          {
            key: 'view-quote',
            type: 'action',
            to: 'view-quote',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineHistory size={16} />
                </div>
                <span className="text-m-r">
                  View Quote <span className="text-m-r text-danger">{getQuoteSuffixByStatus(quoteStatus)}</span>
                </span>
              </div>
            ),
            onAction: () => handleQuoteViewModal(),
          },
        ]
      : []),
    ...(handleQuoteRecallModal
      ? [
          {
            key: 'recall-quote',
            type: 'action',
            to: 'view-quote',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineUndo size={16} />
                </div>
                <span className="text-m-r">Recall quote</span>
              </div>
            ),
            onAction: handleQuoteRecallModal,
          },
        ]
      : []),
    ...(handleClearRoute
      ? [
          {
            key: 'clear-route',
            type: 'action',
            to: 'clear-route',
            label: (
              <div className="flex items-center gap-2">
                <div className="text-base-20">
                  <MdOutlineDelete size={16} />
                </div>
                <span className="text-m-r">Clear unfinished legs and resources</span>
              </div>
            ),
            onAction: () => handleClearRoute(),
          },
        ]
      : []),
  ] as MenuItemType[];
};

type LogisticsActionsMenuProps = {
  isRouteValid: boolean;
};

export const LogisticsActionsMenu = ({ isRouteValid }: LogisticsActionsMenuProps) => {
  const { checkPermission } = useAuthContext();
  const navigate = useNavigate();
  const setActionLoading = useRouteBuilderState.use.setActionLoading();
  const { onOpenModal, setIsLoading, onCloseModal } = useRouteModalActions();
  const { openQuote, quoteDetails } = useQuoteDetails();
  const { mutateAsync: rejectAviation } = useRejectAviationMutation();
  const { mutateAsync: setLightAndSirens } = useLightsAndSirensMutation();
  const { mutateAsync: setNonStopFlight } = useNonStopFlightMutation();
  const { caseId, quote, routePlanningStatus, isLightsSirensRequired, status, nonStopFlightRequired, txCenterDetails } =
    useCaseDetailsWithReviews();
  const { handleClearRouteWithResources } = useLogisticsActions();
  const mainRoute = useRouteDetails(RouteTypeEnum.Main);
  const dryRunRoute = useRouteDetails(RouteTypeEnum.DryRun);
  const { ClinicalSpecialist: specialists, OCSConsole: devices, Surgeon: surgeons } = useRouteResources();

  const quoteStatus = quote?.status;

  const handleLogisticsStatus = useCallback(() => {
    navigate(`/case/${caseId}/planner`);
    onOpenModal({ type: RouteModalTypes.LogisticsStatusLog, context: null });
  }, [caseId, onOpenModal, navigate]);

  const handleLogisticsChangeStatusLog = useCallback(() => {
    onCloseModal();
    navigate(`/case/${caseId}/planner/change-log`);
  }, [navigate, caseId, onCloseModal]);

  const handleOpenDeviceTrackingLog = useCallback(() => {
    onCloseModal();
    navigate(`/case/${caseId}/planner/device-tracking-log`);
  }, [navigate, caseId, onCloseModal]);

  const handleQuoteView = useCallback(() => {
    openQuote({
      rejectedByTxCenter: quoteStatus === QuoteStatus.Rejected,
      isViewOnly: true,
    });
  }, [openQuote, quoteStatus]);

  const handleQuoteRecall = useCallback(() => {
    openQuote({ isViewOnly: true, recallQuote: true });
  }, [openQuote]);

  const handleCopyLink = useCallback(() => {
    copyToClipboard(window.location.href);
    toast.success('Route link copied to clipboard');
  }, []);

  const handleRejectAviation = useCallback(
    async (comment?: string) => {
      try {
        setIsLoading(true);
        await rejectAviation({ caseId, comment });
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message || 'Error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [caseId, rejectAviation, setIsLoading],
  );

  const handleLightAndSirensRequest = useCallback(async () => {
    try {
      setActionLoading(true);

      const newLightAndSirensState = !isLightsSirensRequired;
      await setLightAndSirens({ caseId, isLightsSirensRequired: newLightAndSirensState });

      toast.success(
        newLightAndSirensState
          ? 'Lights and sirens is requested for the case'
          : 'Lights and sirens request was turned off.',
      );
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message || 'Error occurred');
    } finally {
      setActionLoading(false);
    }
  }, [caseId, isLightsSirensRequired, setActionLoading, setLightAndSirens]);

  const handleNonStopFlight = useCallback(async () => {
    try {
      setActionLoading(true);

      const newNonStopFlightRequired = !nonStopFlightRequired;
      await setNonStopFlight({ caseId, nonStopFlightRequired: newNonStopFlightRequired });

      toast.success(
        newNonStopFlightRequired
          ? 'Non-Stop Flight is requested for the case'
          : 'Non-Stop Flight request was turned off.',
      );
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message || 'Error occurred');
    } finally {
      setActionLoading(false);
    }
  }, [caseId, nonStopFlightRequired, setActionLoading, setNonStopFlight]);

  const openRejectAviationModal = useCallback(() => {
    onOpenModal({
      type: RouteModalTypes.QuoteComment,
      context: {
        title: 'Decline Aviation',
        commentRequired: true,
        onAction: handleRejectAviation,
        actionButtonTitle: 'Decline Aviation',
        onClose: onCloseModal,
      },
    });
  }, [handleRejectAviation, onCloseModal, onOpenModal]);

  const isActiveCaseStatus = isAllowedStatus({
    statusFrom: CaseStatus.PendingLogistics,
    statusTo: CaseStatus.ReturningToBase,
    currentStatus: status,
  });

  const canRejectAviation = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionAviationApprove);
    const correctStatus = routePlanningStatus === RoutePlanStatus.WaitingForAviationApproval;

    return hasPermission && correctStatus && isActiveCaseStatus;
  }, [isActiveCaseStatus, checkPermission, routePlanningStatus]);

  const canViewQuote = useMemo(() => {
    const hasPermission = checkPermission(CaseDetailsPermissions.CaseQuoteRead);
    const isCorrectStatus = quoteStatus && quoteStatus !== QuoteStatus.NoQuote;
    return hasPermission && isCorrectStatus;
  }, [quoteStatus, checkPermission]);

  const canRecallQuote = useMemo(() => {
    const hasAirPermission = checkPermission(RouteBuilderPermissions.ActionRecallAirQuote);
    const hasGroundPermission = checkPermission(RouteBuilderPermissions.ActionRecallGroundQuote);
    const isAirQuote = !!quoteDetails?.airQuote;
    const hasCorrectQuoteStatus = quote && quoteStatusesToRecall.includes(quote.status);
    const hasPermission = isAirQuote ? hasAirPermission : hasGroundPermission;
    const canRecallByCaseStatus = isAllowedStatus({
      currentStatus: status,
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.AtTXCenter,
    });

    return hasPermission && hasCorrectQuoteStatus && canRecallByCaseStatus;
  }, [checkPermission, status, quote, quoteDetails]);

  const canViewLightAndSirens = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionLightsAndSirensView);

    return hasPermission && isActiveCaseStatus;
  }, [isActiveCaseStatus, checkPermission]);

  const canSetLightAndSirens = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionLightsAndSirensHandle);

    return hasPermission && isActiveCaseStatus;
  }, [isActiveCaseStatus, checkPermission]);

  const canViewNonStopFlight = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionNonStopFlightView);

    return hasPermission && isActiveCaseStatus && !!txCenterDetails?.nonStopFlightAllowed;
  }, [checkPermission, txCenterDetails, isActiveCaseStatus]);

  const isQuoteGenerated = quoteStatus && ![QuoteStatus.NoQuote, QuoteStatus.Recalled].includes(quoteStatus);

  const canSetNonStopFlight = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionNonStopFlightHandle);

    return hasPermission && isActiveCaseStatus && !isQuoteGenerated && !!txCenterDetails?.nonStopFlightAllowed;
  }, [isActiveCaseStatus, checkPermission, txCenterDetails, isQuoteGenerated]);

  const canClearRoute = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.LegCreate);
    const isCorrectStatus =
      routePlanningStatus &&
      [
        RoutePlanStatus.RoutePlanningStart,
        RoutePlanStatus.PendingResourcesBooking,
        RoutePlanStatus.ResourcesBooked,
      ].includes(routePlanningStatus);
    const hasLegs = !!mainRoute.legs.length || !!dryRunRoute.legs.length;
    const hasHubs = !!(mainRoute.departureHub || mainRoute.arrivalHub || dryRunRoute.arrivalHub);
    const hasResources = !!specialists.length || !!devices.length || !!surgeons.length;
    const hasDataToRemove = hasLegs || hasHubs || hasResources;

    return hasPermission && isCorrectStatus && hasDataToRemove && isActiveCaseStatus;
  }, [
    isActiveCaseStatus,
    checkPermission,
    specialists,
    mainRoute,
    dryRunRoute,
    devices,
    routePlanningStatus,
    surgeons,
  ]);

  const canViewDeviceTrackingLog = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.DeviceTrackingLogView);

    const isDeviceTrackingCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.ResourceDeployed,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isDeviceTrackingCaseStatus;
  }, [checkPermission, status]);

  const menu = useMemo(() => {
    const handleQuoteViewModal = canViewQuote ? handleQuoteView : undefined;
    const handleQuoteRecallModal = canRecallQuote ? handleQuoteRecall : undefined;
    const handleRejectAviationModal = canRejectAviation ? openRejectAviationModal : undefined;
    const handleCopyLinkAction = isRouteValid ? handleCopyLink : undefined;
    const handleLightAndSirens = canSetLightAndSirens ? handleLightAndSirensRequest : undefined;
    const handleClearRoute = canClearRoute ? handleClearRouteWithResources : undefined;
    const handleDeviceTrackingLog = canViewDeviceTrackingLog ? handleOpenDeviceTrackingLog : undefined;
    const handleChangeNonStopFlight = canSetNonStopFlight ? handleNonStopFlight : undefined;

    return getMenu({
      handleLogisticsStatusModal: handleLogisticsStatus,
      handleLogisticsChangeStatusLog,
      handleQuoteViewModal,
      handleCopyLinkAction,
      handleRejectAviationModal,
      handleLightAndSirens,
      handleClearRoute,
      handleQuoteRecallModal,
      handleDeviceTrackingLog,
      quoteStatus,
      isLightsSirensRequired,
      isQuoteGenerated,
      handleChangeNonStopFlight,
      nonStopFlightRequired,
      canViewLightAndSirens,
      canViewNonStopFlight,
    });
  }, [
    canViewQuote,
    handleQuoteView,
    canRejectAviation,
    openRejectAviationModal,
    isRouteValid,
    handleCopyLink,
    canSetLightAndSirens,
    handleLightAndSirensRequest,
    canClearRoute,
    handleClearRouteWithResources,
    handleLogisticsStatus,
    handleLogisticsChangeStatusLog,
    canRecallQuote,
    handleQuoteRecall,
    quoteStatus,
    isLightsSirensRequired,
    isQuoteGenerated,
    canViewDeviceTrackingLog,
    handleOpenDeviceTrackingLog,
    canSetNonStopFlight,
    handleNonStopFlight,
    nonStopFlightRequired,
    canViewLightAndSirens,
    canViewNonStopFlight,
  ]);

  return (
    <ActionMenu
      items={menu}
      MenuTrigger={
        <div className="cursor-pointer">
          <HiOutlineDotsHorizontal />
        </div>
      }
      label="Case Action Menu"
      placement="bottom-start"
      sizeClassName="w-auto"
    />
  );
};
